/**
 * File d'écritures offline-first (créations).
 *
 * Principe : hors ligne, une mutation dont le `networkMode` est "online" (défaut)
 * est mise en PAUSE par TanStack Query au lieu d'échouer, puis rejouée
 * automatiquement au retour du réseau (onlineManager). On s'appuie dessus pour
 * mettre les créations en file sans rien persister sur disque (choix RGPD —
 * mémoire seule).
 *
 * Anti-doublon : chaque création porte une clé d'idempotence STABLE, transmise
 * au serveur via l'en-tête `Idempotency-Key`. Comme la clé fait partie des
 * variables de la mutation, elle est identique lors du rejeu → le serveur
 * ignore le second envoi (voir IdempotentCreateMixin côté back).
 *
 * `setMutationDefaults` sert de filet : si le composant à l'origine de la saisie
 * a été démonté avant la reprise, la mutation retrouve quand même un `mutationFn`
 * et l'invalidation de liste au moment du rejeu.
 */
import {
  onlineManager,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { toast } from "sonner";

/** Variables d'une création offline-capable. */
export type OfflineCreateVars<P> = { payload: P; idempotencyKey: string };

/** Résultat de `submit` : créé en ligne, ou mis en file hors ligne. */
export type SubmitResult<R> =
  | { status: "created"; data: R }
  | { status: "queued" };

/** Clé d'idempotence unique (contexte sécurisé requis pour `crypto.randomUUID`).
 *  Exportée : les créations hors file offline (ordonnances, examens) la posent
 *  aussi, ce qui rend leur rejeu automatique sans doublon (cf. lib/api/client). */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Défauts d'une mutation de création "offline-capable". À appeler une fois au
 * démarrage, par module. Garantit un `mutationFn` et l'invalidation de liste au
 * rejeu même sans observateur (composant démonté).
 */
export function registerOfflineCreate<P, R>(
  qc: QueryClient,
  mutationKey: readonly unknown[],
  createFn: (payload: P, idempotencyKey: string) => Promise<R>,
  invalidateKey: readonly unknown[],
): void {
  qc.setMutationDefaults([...mutationKey], {
    mutationFn: (vars) => {
      const { payload, idempotencyKey } = vars as unknown as OfflineCreateVars<P>;
      return createFn(payload, idempotencyKey);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [...invalidateKey] });
    },
    retry: 0,
    networkMode: "online",
  });
}

/**
 * Promesse résolue à la PROCHAINE bascule hors ligne. Sert à ne pas attendre
 * indéfiniment une mutation que TanStack Query vient de mettre en pause.
 * Renvoie aussi son désabonnement, à appeler dès que la course est tranchée.
 */
function offlineSignal(): { promise: Promise<void>; cancel: () => void } {
  let unsubscribe = () => {};
  const promise = new Promise<void>((resolve) => {
    unsubscribe = onlineManager.subscribe((online) => {
      if (!online) resolve();
    });
  });
  return { promise, cancel: () => unsubscribe() };
}

/**
 * Enveloppe une mutation de création : `submit` porte la clé d'idempotence,
 * met en file sans attendre quand la session est hors ligne (une mutation en
 * pause ne résout jamais sa promesse — on n'`await` donc pas), et renvoie
 * l'entité créée quand on est en ligne.
 *
 * Deux pièges couverts, tous deux vécus en production :
 *
 * 1. **Bascule hors ligne EN COURS de requête.** Le test d'`isOnline` au clic
 *    ne dit rien de la suite : si la requête échoue une fois la session passée
 *    hors ligne, TanStack Query met la mutation en pause et `mutateAsync` ne se
 *    résout JAMAIS. L'appelant restait figé sur son spinner. On court-circuite
 *    donc l'attente dès la bascule : la mutation est bien en file et sera
 *    rejouée, c'est exactement le résultat « queued ».
 *
 * 2. **Clé d'idempotence rejouable.** Une nouvelle clé à chaque tentative
 *    transformait la re-saisie qui suit un échec en DOUBLON quand la première
 *    requête avait en fait abouti côté serveur. La clé est donc attachée au
 *    CONTENU soumis : rejouer la même saisie rejoue la même clé et le serveur
 *    dédoublonne ; soumettre une saisie différente prend une clé neuve.
 *
 *    L'attacher au contenu plutôt qu'au cycle de vie du composant est
 *    délibéré. Une clé simplement « conservée jusqu'au prochain succès »
 *    serait rejouée par la saisie SUIVANTE si l'utilisateur abandonne la
 *    première : le serveur, ayant mis en cache la réponse de la création
 *    réellement aboutie, renverrait alors l'ancienne fiche au lieu de créer
 *    la nouvelle.
 */
export function useOfflineCreate<P, R>(
  mutation: UseMutationResult<R, unknown, OfflineCreateVars<P>>,
): { submit: (payload: P) => Promise<SubmitResult<R>>; isPending: boolean } {
  const attemptRef = useRef<{ payload: string; key: string } | null>(null);
  const queryClient = useQueryClient();

  /** La saisie est-elle RÉELLEMENT en file sous cette clé ? */
  const isQueued = (idempotencyKey: string): boolean =>
    queryClient
      .getMutationCache()
      .getAll()
      .some(
        (m) =>
          m.state.isPaused
          && (m.state.variables as OfflineCreateVars<P> | undefined)?.idempotencyKey
            === idempotencyKey,
      );

  const submit = async (payload: P): Promise<SubmitResult<R>> => {
    const fingerprint = JSON.stringify(payload);
    const previous = attemptRef.current;
    const idempotencyKey =
      previous && previous.payload === fingerprint ? previous.key : newIdempotencyKey();
    // Retenu tant que la tentative n'a pas abouti : c'est ce qui permet à la
    // re-soumission de la MÊME saisie de rejouer la même clé.
    attemptRef.current = { payload: fingerprint, key: idempotencyKey };
    const vars = { payload, idempotencyKey };

    if (!onlineManager.isOnline()) {
      mutation.mutate(vars);
      attemptRef.current = null;
      return { status: "queued" };
    }

    /**
     * Mise en file de rattrapage — le filet du piège n° 1.
     *
     * TanStack Query ne met une mutation en pause que si la session est DÉJÀ
     * hors ligne au moment de partir. Celle qui est partie en ligne puis échoue
     * après la bascule est simplement REJETÉE (`retry: 0`). Le résultat
     * « queued » rendu par la course était donc, dans ce cas précis, un
     * mensonge : l'écran annonçait « sera synchronisé » et la saisie
     * n'existait nulle part. Vérifié par un test qui comptait zéro mutation en
     * pause — c'est une perte de donnée clinique, pas un défaut d'affichage.
     *
     * On remet donc la saisie en file soi-même, sauf si elle y est déjà. Une
     * double mise en file serait de toute façon sans danger : les deux
     * porteraient la même clé d'idempotence et le serveur ne créerait qu'un
     * seul acte.
     */
    const ensureQueued = () => {
      if (!isQueued(idempotencyKey)) mutation.mutate(vars);
      attemptRef.current = null;
    };

    const offline = offlineSignal();
    try {
      const result = await Promise.race([
        mutation
          .mutateAsync(vars)
          .then((data): SubmitResult<R> => ({ status: "created", data })),
        offline.promise.then((): SubmitResult<R> => ({ status: "queued" })),
      ]);
      if (result.status === "queued") ensureQueued();
      else attemptRef.current = null;
      return result;
    } catch (error) {
      // Échec alors que la session a basculé entre-temps — réseau perdu, ou
      // serveur devenu injoignable (cf. lib/api/server-reachability, qui bascule
      // le manager quand l'API ne répond plus). La saisie part en file au lieu
      // d'être perdue ; l'agent continue de travailler.
      if (!onlineManager.isOnline()) {
        ensureQueued();
        return { status: "queued" };
      }
      throw error;
    } finally {
      offline.cancel();
    }
  };
  return { submit, isPending: mutation.isPending };
}

/** Nombre d'écritures en file (mises en pause hors ligne, pas encore synchronisées). */
export function useOfflinePendingCount(): number {
  const qc = useQueryClient();
  return useSyncExternalStore(
    (onChange) => qc.getMutationCache().subscribe(() => onChange()),
    () => qc.getMutationCache().getAll().filter((m) => m.state.isPaused).length,
    () => 0,
  );
}

/**
 * Avertit avant de quitter/fermer l'onglet tant que des écritures sont en file
 * (non synchronisées). En mémoire seule, fermer hors ligne perdrait la file —
 * la confirmation native du navigateur évite une perte accidentelle.
 */
export function useUnsavedQueueGuard(): void {
  const pending = useOfflinePendingCount();
  useEffect(() => {
    if (pending <= 0) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pending]);
}

// Mutations passées par l'état "pause" (mises en file hors ligne) : sert à
// n'annoncer la synchro que pour celles-là, pas pour une création en ligne.
const pausedSeen = new WeakSet<object>();

/**
 * Observe la file : signale par un toast quand une écriture mise en file hors
 * ligne finit par passer au retour du réseau. Renvoie une fonction de nettoyage.
 */
export function watchOfflineSync(qc: QueryClient): () => void {
  return qc.getMutationCache().subscribe((event) => {
    const mutation = event.mutation;
    if (!mutation) return;
    if (mutation.state.isPaused) {
      pausedSeen.add(mutation);
      return;
    }
    if (mutation.state.status === "success" && pausedSeen.has(mutation)) {
      pausedSeen.delete(mutation);
      toast.success("Synchronisé — opération enregistrée hors ligne envoyée.");
    }
  });
}
